import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './pages.css';
import config, { setRunnerUrl } from '../Config';
import { setTokenCookie } from '../functions/authSession';
import { cognitoSignup, cognitoConfirmSignup, cognitoResendCode, cognitoLogin } from '../functions/cognitoAuth';
import { dedupedFetch } from '../functions/dedupedFetch';
import { pickRunnerUrlFromUserInfo } from '../functions/cognitoIdToken';
import { persistUserIdFromClusters } from '../functions/clusterSwitch';
import { clearRestrictedSession, isRestrictedResponse } from '../functions/restrictedAccess';
import Turnstile, { turnstileEnabled } from '../components/Turnstile';
import Loader from './Loader';

export default function Signup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState('');
  const [botToken, setBotToken] = useState('');
  const botRef = useRef(null);

  const handleSignup = async () => {
    if (!email) { setError('Please enter your email.'); return; }
    if (!password || password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (turnstileEnabled() && !botToken) {
      setError('Please complete the verification challenge below.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await cognitoSignup(email, password, botToken);
      setStep(2);
    } catch (err) {
      // The token is single use, so a retry needs a whole new challenge.
      botRef.current?.reset();
      setError(err.message || 'Signup failed. Please try again.');
    }
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (!verifyCode) { setError('Please enter the verification code.'); return; }
    setLoading(true);
    setError('');
    try {
      await cognitoConfirmSignup(email, verifyCode);

      const tokens = await cognitoLogin(email, password);
      setTokenCookie(tokens.idToken);

      let restricted = false;
      try {
        const res = await dedupedFetch(`${config.AUTH_API}/v1/signup`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${tokens.idToken}`,
          },
        });
        restricted = await isRestrictedResponse(res);
      } catch { /* non-critical — account will be created on first login if needed */ }

      try {
        const res = await dedupedFetch(`${config.AUTH_API}/v1/get-user-info`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${tokens.idToken}`,
          },
        });
        restricted = restricted || await isRestrictedResponse(res);
        if (res.ok) {
          const info = await res.json();
          const url = pickRunnerUrlFromUserInfo(info, tokens.idToken);
          if (url) setRunnerUrl(url);
          persistUserIdFromClusters(info);
        }
      } catch { /* non-critical */ }

      // No account record was created for a rejected address, so send the user to the
      // explanation instead of a dashboard that cannot load.
      if (restricted) {
        clearRestrictedSession(email);
        setLoading(false);
        navigate('/not-authorized');
        return;
      }

      navigate('/');
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setLoading(true);
    setError('');
    try {
      await cognitoResendCode(email);
    } catch (err) {
      setError(err.message || 'Failed to resend code.');
    }
    setLoading(false);
  };

  return (
    <div className="signin-main">
      <Loader show={loading} />

      <div className="auth-left">
        <div className="auth-left-top">
          <Link to="/" className="auth-left-logo">Mermadic</Link>
          <span className="auth-left-badge">Managed cloud infrastructure</span>
        </div>
        <div className="auth-left-body">
          <h1 className="auth-left-headline">
            Dedicated clusters.<br />Private VPCs.<br />Fully managed.
          </h1>
          <p className="auth-left-desc">
            Run jobs, deploy services, and automate pipelines on dedicated infrastructure — provisioned in minutes, managed entirely by Mermadic.
          </p>
          <ul className="auth-left-bullets">
            <li>Isolated cluster and private VPC per account</li>
            <li>Jobs, deployments, and CDCI in one platform</li>
            <li>No cloud account or DevOps experience needed</li>
          </ul>
        </div>
      </div>

      <div className="login-container">
        <div className="login-card">
          <Link to="/" className="auth-compact-brand">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to home
          </Link>

          <div className="signup-steps">
            <div className={`signup-step-dot ${step >= 1 ? 'active' : ''}`}>1</div>
            <div className={`signup-step-line ${step >= 2 ? 'active' : ''}`} />
            <div className={`signup-step-dot ${step >= 2 ? 'active' : ''}`}>2</div>
          </div>

          {step === 1 ? (
            <>
              <h2 className="signin-header">Create your account</h2>
              <p className="signin-sub">Start running jobs in minutes — free to try</p>

              <div className="cnt-form">
                <div className="login-field">
                  <label htmlFor="email">Work email</label>
                  <input
                    type="email" id="email" placeholder="you@example.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <div className="login-field">
                  <label htmlFor="password">Password</label>
                  <div className="pw-field">
                    <input
                      type={showPassword ? 'text' : 'password'} id="password" placeholder="Min. 8 characters"
                      value={password} onChange={e => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="pw-toggle"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => setShowPassword(p => !p)}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div className="login-field">
                  <label htmlFor="confirm_password">Confirm password</label>
                  <div className="pw-field">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'} id="confirm_password" placeholder="Re-enter password"
                      value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="pw-toggle"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showConfirmPassword}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => setShowConfirmPassword(p => !p)}
                    >
                      {showConfirmPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                <Turnstile ref={botRef} onToken={setBotToken} action="signup" />

                {error && <p className="signup-error">{error}</p>}

                <button className="login-submit-btn" onClick={handleSignup} disabled={loading}>
                  Continue
                </button>
                <p className="login-legal-hint">
                  By continuing you agree to our <Link to="/terms">Terms of Service</Link> and{' '}
                  <Link to="/privacy">Privacy Policy</Link>.
                </p>
                <p className="login-signup-hint">
                  Already have an account? <Link to="/login">Log in</Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <h2 className="signin-header">Check your email</h2>
              <p className="signin-sub">
                We sent a 6-digit code to<br />
                <strong>{email}</strong>
              </p>

              <div className="cnt-form">
                <div className="login-field">
                  <label htmlFor="verify_code">Verification code</label>
                  <input
                    type="text" id="verify_code" placeholder="123456"
                    value={verifyCode}
                    onChange={e => { setVerifyCode(e.target.value); setError(''); }}
                    maxLength={6}
                    className="signup-code-input"
                  />
                </div>

                {error && <p className="signup-error">{error}</p>}

                <button className="login-submit-btn" onClick={handleConfirm} disabled={loading}>
                  Verify &amp; create account
                </button>
                <p className="login-signup-hint">
                  Didn't get it?{' '}
                  <button className="login-resend-btn" onClick={handleResend} disabled={loading}>
                    Resend code
                  </button>
                </p>
                <p className="login-signup-hint" style={{ marginTop: '0.4rem' }}>
                  <button className="login-resend-btn" onClick={() => setStep(1)}>
                    ← Back
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
